import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, LinearProgress, Alert, IconButton, Tooltip,
  Collapse, Divider, Select, FormControl, InputLabel, ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  TrackChanges, Add, Edit, Delete, Close, ExpandMore, ExpandLess,
  CheckCircle, Warning, TrendingDown, TrendingUp, TrendingFlat,
  Business, Group, Person, People,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

// ── Constants ─────────────────────────────────────────────────────────────────

const GOAL_TYPES: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  company:    { label: 'Company',    color: '#1a237e', icon: <Business fontSize="small" /> },
  department: { label: 'Department', color: '#1b5e20', icon: <Group fontSize="small" /> },
  team:       { label: 'Team',       color: '#e65100', icon: <People fontSize="small" /> },
  individual: { label: 'Individual', color: '#4a148c', icon: <Person fontSize="small" /> },
};

const STATUS_CFG: Record<string, { label: string; color: any }> = {
  not_started: { label: 'Draft',      color: 'default' },
  in_progress: { label: 'On Track',   color: 'success' },
  at_risk:   { label: 'At Risk',    color: 'warning' },
  at_risk_off: { label: 'Off Track',  color: 'error' },
  completed: { label: 'Completed',  color: 'primary' },
  cancelled: { label: 'Cancelled',  color: 'default' },
};

const KR_TYPES = ['numeric', 'percent', 'boolean', 'currency'];
const CYCLES = ['q1', 'q2', 'q3', 'q4', 'h1', 'h2', 'annual'];
const CONFIDENCE_CFG: Record<string, { color: string; icon: React.ReactNode }> = {
  high:   { color: '#2e7d32', icon: <TrendingUp fontSize="small" /> },
  medium: { color: '#f57c00', icon: <TrendingFlat fontSize="small" /> },
  low:    { color: '#c62828', icon: <TrendingDown fontSize="small" /> },
};

function progressColor(p: number): string {
  if (p >= 70) return '#2e7d32';
  if (p >= 40) return '#f57c00';
  return '#c62828';
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface KrUpdate { id: string; newValue: number; previousValue: number; confidence: string; note?: string; createdAt: string; author?: { firstName: string; lastName: string }; }
interface KeyResult { id: string; goalId: string; title: string; type: string; startValue: number; targetValue: number; currentValue: number; unit?: string; progress: number; isDone: boolean; weight: number; updates?: KrUpdate[]; }
interface Goal { id: string; title: string; description?: string; type: string; status: string; cycle: string; year: number; progress: number; weight: number; isPrivate?: boolean; ownerId?: string; department?: string; startDate?: string; dueDate?: string; lastUpdateNote?: string; lastUpdatedAt?: string; owner?: { id: string; firstName: string; lastName: string }; keyResults?: KeyResult[]; parentGoal?: Goal; }
interface Stats { total: number; active: number; completed: number; atRisk: number; offTrack: number; avgProgress: number; byType: Array<{ type: string; count: number; avgProgress: number }>; }

// ── Sub-components ────────────────────────────────────────────────────────────

function ProgressBar({ value, height = 8 }: { value: number; height?: number }) {
  const col = progressColor(value);
  return (
    <Box sx={{ position: 'relative', height, borderRadius: height / 2, bgcolor: '#e0e0e0', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${value}%`, bgcolor: col, transition: 'width 0.4s', borderRadius: height / 2 }} />
    </Box>
  );
}

function StatCard({ label, value, color, icon, sub }: any) {
  return (
    <Card sx={{ textAlign: 'center', p: 0.5 }}>
      <CardContent sx={{ pb: '8px !important' }}>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {sub !== undefined && <Typography variant="body2" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

// ── Key Result Row ────────────────────────────────────────────────────────────

function KrRow({ kr, onCheckIn, onEdit, onDelete }: { kr: KeyResult; onCheckIn: (kr: KeyResult) => void; onEdit: (kr: KeyResult) => void; onDelete: (id: string) => void; }) {
  const [showHistory, setShowHistory] = useState(false);
  const p = kr.progress;
  const display = kr.type === 'boolean'
    ? (kr.isDone ? '✅ Done' : '⬜ Not done')
    : `${Number(kr.currentValue).toLocaleString()}${kr.unit ? ` ${kr.unit}` : ''} / ${Number(kr.targetValue).toLocaleString()}${kr.unit ? ` ${kr.unit}` : ''}`;

  return (
    <Box sx={{ mb: 1.5, pl: 2, borderLeft: `3px solid ${progressColor(p)}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600}>{kr.title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
            <Box sx={{ flex: 1 }}><ProgressBar value={p} height={6} /></Box>
            <Typography variant="caption" fontWeight={700} color={progressColor(p)} sx={{ minWidth: 35 }}>{Math.round(p)}%</Typography>
            <Typography variant="caption" color="text.secondary">{display}</Typography>
          </Box>
        </Box>
        <Button size="small" variant="outlined" sx={{ fontSize: '0.7rem', py: 0 }} onClick={() => onCheckIn(kr)}>Check In</Button>
        <IconButton size="small" onClick={() => onEdit(kr)}><Edit sx={{ fontSize: 14 }} /></IconButton>
        <IconButton size="small" color="error" onClick={() => onDelete(kr.id)}><Delete sx={{ fontSize: 14 }} /></IconButton>
        {kr.updates && kr.updates.length > 0 && (
          <IconButton size="small" onClick={() => setShowHistory(p => !p)}>
            {showHistory ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
          </IconButton>
        )}
      </Box>
      <Collapse in={showHistory}>
        <Box sx={{ mt: 0.5, pl: 1 }}>
          {kr.updates?.slice(-3).reverse().map(u => (
            <Box key={u.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.3 }}>
              <Box sx={{ color: CONFIDENCE_CFG[u.confidence]?.color ?? '#666' }}>
                {CONFIDENCE_CFG[u.confidence]?.icon}
              </Box>
              <Typography variant="caption">
                {Number(u.previousValue).toLocaleString()} → <b>{Number(u.newValue).toLocaleString()}</b>
                {u.note && ` — ${u.note}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {new Date(u.createdAt).toLocaleDateString('en-HK')}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Goal Card ─────────────────────────────────────────────────────────────────

function GoalCard({ goal, onEdit, onDelete, onAddKr, onCheckIn, onEditKr, onDeleteKr, employees, expanded, onToggle }: any) {
  const typeCfg = GOAL_TYPES[goal.type] ?? GOAL_TYPES.individual;
  const statusCfg = STATUS_CFG[goal.status] ?? STATUS_CFG.draft;
  const p = Math.round(Number(goal.progress));

  return (
    <Paper sx={{ mb: 1.5, overflow: 'hidden', border: `1px solid ${progressColor(p)}30` }}>
      <Box sx={{ p: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 1 }} onClick={onToggle}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Box sx={{ color: typeCfg.color }}>{typeCfg.icon}</Box>
            <Typography fontWeight={700} sx={{ flex: 1 }}>{goal.title}</Typography>
            <Chip label={typeCfg.label} size="small"
              sx={{ bgcolor: typeCfg.color, color: '#fff', fontSize: '0.65rem' }} />
            <Chip label={statusCfg.label} size="small" color={statusCfg.color} />
            {goal.owner && (
              <Typography variant="caption" color="text.secondary">
                {goal.owner.firstName} {goal.owner.lastName}
              </Typography>
            )}
          </Box>
          {goal.description && !expanded && (
            <Typography variant="caption" color="text.secondary" noWrap>{goal.description}</Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Box sx={{ flex: 1 }}><ProgressBar value={p} /></Box>
            <Typography variant="body2" fontWeight={700} color={progressColor(p)}>{p}%</Typography>
            <Typography variant="caption" color="text.secondary">
              {goal.keyResults?.length ?? 0} KRs
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={e => e.stopPropagation()}>
          <Tooltip title="Add Key Result"><IconButton size="small" color="primary" onClick={onAddKr}><Add sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Edit"><IconButton size="small" onClick={onEdit}><Edit sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={onDelete}><Delete sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          {expanded ? <ExpandLess sx={{ mt: 0.5 }} /> : <ExpandMore sx={{ mt: 0.5 }} />}
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 1.5 }}>
          {goal.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{goal.description}</Typography>}
          {goal.dueDate && <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Due: {new Date(goal.dueDate + 'T00:00:00').toLocaleDateString('en-HK')}</Typography>}

          {(goal.keyResults?.length ?? 0) === 0 ? (
            <Box sx={{ textAlign: 'center', py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">No key results yet.</Typography>
              <Button size="small" startIcon={<Add />} onClick={onAddKr}>Add Key Result</Button>
            </Box>
          ) : (
            goal.keyResults.map((kr: KeyResult) => (
              <KrRow key={kr.id} kr={kr}
                onCheckIn={() => onCheckIn(kr)}
                onEdit={() => onEditKr(kr)}
                onDelete={onDeleteKr} />
            ))
          )}

          {goal.lastUpdateNote && (
            <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Last note: {goal.lastUpdateNote}</Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function GoalsPage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const currentYear = new Date().getFullYear();
  const [tab, setTab] = useState(0);
  const [cycle, setCycle] = useState('q2');
  const [year, setYear] = useState(currentYear);
  const [typeFilter, setTypeFilter] = useState('');
  const [goals, setGoals] = useState<Goal[]>([]);
  const [myGoals, setMyGoals] = useState<Goal[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Dialogs
  const [goalDialog, setGoalDialog] = useState<Partial<Goal> & { keyResults?: any[] } | null>(null);
  const [krDialog, setKrDialog] = useState<{ goalId: string; kr?: Partial<KeyResult> } | null>(null);
  const [checkInDialog, setCheckInDialog] = useState<{ kr: KeyResult; newValue: string; confidence: string; note: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [all, mine, st] = await Promise.all([
        apiFetch(`/api/goals?cycle=${cycle}&year=${year}${typeFilter ? `&type=${typeFilter}` : ''}`),
        user?.employee?.id ? apiFetch(`/api/goals?ownerId=${user.employee.id}`) : Promise.resolve([]),
        apiFetch(`/api/goals/stats?cycle=${cycle}&year=${year}`),
      ]);
      setGoals(all); setMyGoals(mine); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [cycle, year, typeFilter, user?.employee?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isHR) apiFetch('/api/employees?limit=500').then(r => setEmployees(r.data || r || [])).catch(() => {});
  }, [isHR]);

  const toggle = (id: string) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  // Goal CRUD
  const saveGoal = async () => {
    if (!goalDialog) return;
    try {
      if (goalDialog.id) {
        await apiFetch(`/api/goals/${goalDialog.id}`, { method: 'PATCH', body: JSON.stringify(goalDialog) });
      } else {
        await apiFetch('/api/goals', { method: 'POST', body: JSON.stringify(goalDialog) });
      }
      setGoalDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const deleteGoal = async (id: string) => {
    if (!window.confirm('Delete this goal and all its key results?')) return;
    try { await apiFetch(`/api/goals/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  // KR CRUD
  const saveKr = async () => {
    if (!krDialog) return;
    try {
      if (krDialog.kr?.id) {
        await apiFetch(`/api/goals/key-results/${krDialog.kr.id}`, { method: 'PATCH', body: JSON.stringify(krDialog.kr) });
      } else {
        await apiFetch(`/api/goals/${krDialog.goalId}/key-results`, { method: 'POST', body: JSON.stringify(krDialog.kr) });
      }
      setKrDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const deleteKr = async (id: string) => {
    try { await apiFetch(`/api/goals/key-results/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  // Check-in
  const submitCheckIn = async () => {
    if (!checkInDialog) return;
    try {
      await apiFetch(`/api/goals/key-results/${checkInDialog.kr.id}/check-in`, {
        method: 'POST',
        body: JSON.stringify({
          newValue: checkInDialog.kr.type === 'boolean'
            ? (checkInDialog.newValue === 'done' ? 1 : 0)
            : Number(checkInDialog.newValue),
          confidence: checkInDialog.confidence,
          note: checkInDialog.note,
        }),
      });
      setCheckInDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const openGoalDialog = (g?: Goal) => {
    setGoalDialog(g ? { ...g } : {
      type: 'individual', status: 'active', cycle, year,
      weight: 1, isPrivate: false,
    });
  };

  const openNewKr = (goalId: string) => {
    setExpanded(p => ({ ...p, [goalId]: true }));
    setKrDialog({ goalId, kr: { type: 'numeric', startValue: 0, targetValue: 100, currentValue: 0, weight: 1, unit: '' } });
  };

  const displayGoals = tab === 0 ? goals.filter(g => !typeFilter || g.type === typeFilter) : myGoals;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrackChanges sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Goals & OKRs</Typography>
            <Typography variant="body2" color="text.secondary">Objectives & Key Results — align the company, teams, and individuals</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#1a237e' }} onClick={() => openGoalDialog()}>
          New Objective
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Total" value={stats.total} color="#1a237e" icon={<TrackChanges />} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="On Track" value={stats.active} color="#2e7d32" icon={<CheckCircle />} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="At Risk" value={stats.atRisk} color="#f57c00" icon={<Warning />} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Off Track" value={stats.offTrack} color="#c62828" icon={<TrendingDown />} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Completed" value={stats.completed} color="#0288d1" icon={<CheckCircle />} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <StatCard label="Avg Progress" value={`${stats.avgProgress}%`} color={progressColor(stats.avgProgress)} icon={<TrendingUp />} />
          </Grid>
        </Grid>
      )}

      {/* Type mini-bars */}
      {stats && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Progress by Level</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {stats.byType.filter(b => b.count > 0).map(b => (
              <Box key={b.type} sx={{ flex: 1, minWidth: 120 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', color: GOAL_TYPES[b.type]?.color }}>
                    {GOAL_TYPES[b.type]?.icon}
                    <Typography variant="caption" fontWeight={600}>{GOAL_TYPES[b.type]?.label}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">{b.count} · {b.avgProgress}%</Typography>
                </Box>
                <ProgressBar value={b.avgProgress} height={6} />
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Cycle</InputLabel>
          <Select value={cycle} label="Cycle" onChange={e => setCycle(e.target.value)}>
            {CYCLES.map(c => <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select value={year} label="Year" onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <ToggleButtonGroup size="small" exclusive value={typeFilter} onChange={(_, v) => setTypeFilter(v ?? '')}>
          <ToggleButton value="">All</ToggleButton>
          {Object.entries(GOAL_TYPES).map(([k, v]) => (
            <ToggleButton key={k} value={k} sx={{ gap: 0.5 }}>
              {v.icon} {v.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="All OKRs" />
          <Tab label="My Objectives" />
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Goal list */}
      {displayGoals.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <TrackChanges sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography>No objectives found for this cycle.</Typography>
          <Button startIcon={<Add />} onClick={() => openGoalDialog()} sx={{ mt: 1 }}>
            Create First Objective
          </Button>
        </Paper>
      ) : (
        displayGoals.map(g => (
          <GoalCard
            key={g.id}
            goal={g}
            expanded={!!expanded[g.id]}
            onToggle={() => toggle(g.id)}
            onEdit={() => openGoalDialog(g)}
            onDelete={() => deleteGoal(g.id)}
            onAddKr={() => openNewKr(g.id)}
            onCheckIn={(kr: KeyResult) => setCheckInDialog({ kr, newValue: String(kr.currentValue), confidence: 'high', note: '' })}
            onEditKr={(kr: KeyResult) => setKrDialog({ goalId: g.id, kr: { ...kr } })}
            onDeleteKr={deleteKr}
            employees={employees}
          />
        ))
      )}

      {/* ── GOAL DIALOG ── */}
      <Dialog open={!!goalDialog} onClose={() => setGoalDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {goalDialog?.id ? 'Edit Objective' : 'New Objective'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setGoalDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Title" fullWidth required value={goalDialog?.title ?? ''}
              onChange={e => setGoalDialog(p => ({ ...p, title: e.target.value }))} />
            <TextField label="Description" fullWidth multiline rows={2} value={goalDialog?.description ?? ''}
              onChange={e => setGoalDialog(p => ({ ...p, description: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Type" fullWidth value={goalDialog?.type ?? 'individual'}
                onChange={e => setGoalDialog(p => ({ ...p, type: e.target.value }))}>
                {Object.entries(GOAL_TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
              <TextField select label="Status" fullWidth value={goalDialog?.status ?? 'active'}
                onChange={e => setGoalDialog(p => ({ ...p, status: e.target.value }))}>
                {Object.entries(STATUS_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Cycle" fullWidth value={goalDialog?.cycle ?? 'q2'}
                onChange={e => setGoalDialog(p => ({ ...p, cycle: e.target.value }))}>
                {CYCLES.map(c => <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>)}
              </TextField>
              <TextField select label="Year" fullWidth value={goalDialog?.year ?? currentYear}
                onChange={e => setGoalDialog(p => ({ ...p, year: Number(e.target.value) }))}>
                {[2024, 2025, 2026, 2027].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Box>
            {isHR && (
              <TextField select label="Owner" fullWidth value={goalDialog?.ownerId ?? ''}
                onChange={e => setGoalDialog(p => ({ ...p, ownerId: e.target.value || undefined }))}>
                <MenuItem value="">— Company / Team level —</MenuItem>
                {employees.map((emp: any) => <MenuItem key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Department (optional)" fullWidth value={goalDialog?.department ?? ''}
              onChange={e => setGoalDialog(p => ({ ...p, department: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={goalDialog?.startDate ?? ''}
                onChange={e => setGoalDialog(p => ({ ...p, startDate: e.target.value || undefined }))} />
              <TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={goalDialog?.dueDate ?? ''}
                onChange={e => setGoalDialog(p => ({ ...p, dueDate: e.target.value || undefined }))} />
            </Box>
            <TextField label="Last Update Note" fullWidth value={goalDialog?.lastUpdateNote ?? ''}
              onChange={e => setGoalDialog(p => ({ ...p, lastUpdateNote: e.target.value }))}
              placeholder="Optional weekly status note..." />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoalDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#1a237e' }} onClick={saveGoal} disabled={!goalDialog?.title}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── KR DIALOG ── */}
      <Dialog open={!!krDialog} onClose={() => setKrDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {krDialog?.kr?.id ? 'Edit Key Result' : 'Add Key Result'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setKrDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Title" fullWidth required value={krDialog?.kr?.title ?? ''}
              onChange={e => setKrDialog(p => p ? { ...p, kr: { ...p.kr, title: e.target.value } } : null)} />
            <TextField select label="Type" fullWidth value={krDialog?.kr?.type ?? 'numeric'}
              onChange={e => setKrDialog(p => p ? { ...p, kr: { ...p.kr, type: e.target.value } } : null)}>
              {KR_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            {krDialog?.kr?.type !== 'boolean' && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Start Value" type="number" fullWidth value={krDialog?.kr?.startValue ?? 0}
                    onChange={e => setKrDialog(p => p ? { ...p, kr: { ...p.kr, startValue: Number(e.target.value) } } : null)} />
                  <TextField label="Target Value" type="number" fullWidth value={krDialog?.kr?.targetValue ?? 100}
                    onChange={e => setKrDialog(p => p ? { ...p, kr: { ...p.kr, targetValue: Number(e.target.value) } } : null)} />
                </Box>
                <TextField label="Unit (e.g. %, HKD, users)" fullWidth value={krDialog?.kr?.unit ?? ''}
                  onChange={e => setKrDialog(p => p ? { ...p, kr: { ...p.kr, unit: e.target.value } } : null)} />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKrDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#1a237e' }} onClick={saveKr} disabled={!krDialog?.kr?.title}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── CHECK-IN DIALOG ── */}
      <Dialog open={!!checkInDialog} onClose={() => setCheckInDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Check In: {checkInDialog?.kr.title}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setCheckInDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {checkInDialog?.kr.type === 'boolean' ? (
              <TextField select label="Status" fullWidth value={checkInDialog.newValue}
                onChange={e => setCheckInDialog(p => p ? { ...p, newValue: e.target.value } : null)}>
                <MenuItem value="done">✅ Done</MenuItem>
                <MenuItem value="not_done">⬜ Not Done</MenuItem>
              </TextField>
            ) : (
              <TextField label={`New Value${checkInDialog?.kr.unit ? ` (${checkInDialog.kr.unit})` : ''}`}
                type="number" fullWidth value={checkInDialog?.newValue ?? ''}
                onChange={e => setCheckInDialog(p => p ? { ...p, newValue: e.target.value } : null)}
                helperText={`Current: ${checkInDialog?.kr.currentValue} → Target: ${checkInDialog?.kr.targetValue}`} />
            )}
            <TextField select label="Confidence" fullWidth value={checkInDialog?.confidence ?? 'high'}
              onChange={e => setCheckInDialog(p => p ? { ...p, confidence: e.target.value } : null)}>
              {Object.entries(CONFIDENCE_CFG).map(([k, v]) => (
                <MenuItem key={k} value={k} sx={{ color: v.color }}>
                  {v.icon} {k === 'high' ? '🟢 On Track' : k === 'medium' ? '🟡 At Risk' : '🔴 Off Track'}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Note (optional)" fullWidth multiline rows={2} value={checkInDialog?.note ?? ''}
              onChange={e => setCheckInDialog(p => p ? { ...p, note: e.target.value } : null)}
              placeholder="What happened? What's blocking?" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckInDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#1a237e' }} onClick={submitCheckIn}>Submit Check-In</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
